
import React, { useState, useRef, useEffect } from 'react';
import { Community, JoinSetting, CommunityTheme } from '../types';
import { CITIES } from '../constants';
import { supabase } from '../services/supabaseClient';

export type CommunityFormData = Omit<Community, 'id' | 'status' | 'memberCount' | 'creatorId'>;

interface CommunityFormProps {
  onSave: (data: CommunityFormData) => void | Promise<void>;
  onCancel: () => void;
  initialData?: Community | null;
}

export const CommunityForm: React.FC<CommunityFormProps> = ({ onSave, onCancel, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<CommunityFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    targetAudience: initialData?.targetAudience || '',
    logo: initialData?.logo || '',
    tgLink: initialData?.tgLink || '',
    joinSetting: (initialData?.joinSetting as JoinSetting) || 'auto',
    city: initialData?.city || CITIES[0],
    theme: (initialData?.theme as CommunityTheme) || 'any'
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description,
        targetAudience: initialData.targetAudience,
        logo: initialData.logo,
        tgLink: initialData.tgLink,
        joinSetting: initialData.joinSetting,
        city: initialData.city,
        theme: initialData.theme
      });
    }
  }, [initialData]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = e.target.files?.[0];
      if (!file) return;
      const fileName = `logos/${crypto.randomUUID()}.${file.name.split('.').pop()}`;
      const { error } = await supabase.storage.from('logos').upload(fileName, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(fileName);
      setFormData(prev => ({ ...prev, logo: publicUrl }));
    } catch (err: any) {
      alert('Ошибка загрузки лого: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.logo) {
      alert('Заполните название и загрузите логотип!');
      return;
    }
    setLoading(true);
    try {
      await onSave(formData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const labelClasses = "block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1";
  const inputClasses = "w-full px-5 py-4 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-red-500 font-medium text-sm transition-all";

  return (
    <div className="bg-white rounded-[2.5rem] p-4 md:p-2 animate-fadeIn">
      <h2 className="text-3xl font-black text-gray-900 mb-2 uppercase tracking-tighter">
        {initialData ? 'Настройка клуба' : 'Создание клуба'}
      </h2>
      <p className="text-xs text-gray-500 mb-8 italic">Сообщество — это сердце нетворкинга.</p>
      
      <div className="space-y-6">
        <div className="flex flex-col items-center mb-8">
          <div 
            onClick={() => fileInputRef.current?.click()} 
            className="w-32 h-32 rounded-[2.5rem] bg-gray-50 border-2 border-dashed border-red-100 flex items-center justify-center cursor-pointer overflow-hidden relative shadow-inner hover:border-red-500 transition-colors group"
          >
            {formData.logo ? (
              <img src={formData.logo} className="w-full h-full object-cover group-hover:opacity-50 transition-opacity" alt="Logo" />
            ) : (
              <div className="text-center">
                <span className="text-3xl block mb-1">🖼️</span>
                <span className="text-[8px] font-black uppercase text-gray-400">Логотип</span>
              </div>
            )}
            {uploading && <div className="absolute inset-0 bg-white/80 flex items-center justify-center">◌</div>}
          </div>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelClasses}>Название</label>
            <input type="text" className={inputClasses} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Напр: IT-Нячанг" />
          </div>
          <div>
            <label className={labelClasses}>Telegram ссылка</label>
            <input type="text" className={inputClasses} value={formData.tgLink} onChange={e => setFormData({...formData, tgLink: e.target.value})} placeholder="t.me/your_group" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClasses}>Город</label>
            <select className={inputClasses} value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})}>
              {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClasses}>Тип входа</label>
            <select className={inputClasses} value={formData.joinSetting} onChange={e => setFormData({...formData, joinSetting: e.target.value as JoinSetting})}>
              <option value="auto">Свободный</option>
              <option value="manual">Через заявку</option>
            </select>
          </div>
          <div>
            <label className={labelClasses}>Тематика</label>
            <select className={inputClasses} value={formData.theme} onChange={e => setFormData({...formData, theme: e.target.value as CommunityTheme})}>
              <option value="any">Общая</option>
              <option value="business">Бизнес</option>
              <option value="lifestyle">Лайфстайл</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelClasses}>Целевая аудитория</label>
          <input type="text" className={inputClasses} value={formData.targetAudience} onChange={e => setFormData({...formData, targetAudience: e.target.value})} placeholder="Напр: Предприниматели и фрилансеры" />
        </div>
        
        <div>
          <label className={labelClasses}>Краткое описание</label>
          <textarea 
            className={`${inputClasses} h-24 resize-none`} 
            value={formData.description} 
            onChange={e => setFormData({...formData, description: e.target.value})} 
            placeholder="О чем ваше сообщество?"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button 
            onClick={handleSave} 
            disabled={loading || uploading} 
            className="flex-1 bg-black text-white font-black py-5 rounded-2xl uppercase text-[10px] tracking-widest shadow-xl hover:bg-red-600 transition-all disabled:opacity-50"
          >
            {loading ? 'СОХРАНЕНИЕ...' : 'ПОДТВЕРДИТЬ'}
          </button>
          <button 
            onClick={onCancel} 
            className="px-8 py-5 border-2 border-black text-black font-black rounded-2xl uppercase text-[10px] tracking-widest hover:bg-gray-50 transition-all"
          >
            ОТМЕНА
          </button>
        </div>
      </div>
    </div>
  );
};
